import { useState, useRef } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Capacitor } from '@capacitor/core';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const functions = getFunctions();
const scanReceipt = httpsCallable(functions, 'scanReceipt');

const CATEGORIES = [
  'Fuel & Gas', 'Vehicle Maintenance', 'Insurance', 'Tolls & Parking',
  'Phone & Data', 'Meals & Entertainment', 'Office Supplies', 'Software & Subscriptions',
  'Professional Services', 'Travel & Lodging', 'Equipment & Tools', 'Other',
];

export default function AddExpense() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const isNative = Capacitor.isNativePlatform();

  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);

  const [expense, setExpense] = useState({
    merchant: '',
    date: new Date().toISOString().split('T')[0],
    total: '',
    category: '',
    notes: '',
  });

  function updateField(field, value) {
    setExpense((prev) => ({ ...prev, [field]: value }));
  }

  async function handleFile(file) {
    if (!file) return;
    setError('');

    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10MB.');
      return;
    }

    setScanning(true);

    // Preview
    if (file.type.startsWith('image/')) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }

    try {
      const base64 = await fileToBase64(file);
      const result = await scanReceipt({
        imageBase64: base64,
        mimeType: file.type,
        fileName: file.name,
      });

      const data = result.data;
      setExpense((prev) => ({
        ...prev,
        merchant: data.merchant || prev.merchant,
        date: data.date || prev.date,
        total: data.total ? data.total.toString() : prev.total,
        category: data.category || prev.category,
      }));
    } catch (err) {
      console.error('Scan error:', err);
      setError('Failed to scan receipt. You can enter details manually.');
    }
    setScanning(false);
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function handleSave() {
    if (!expense.total || !expense.category) {
      setError('Amount and category are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await addDoc(collection(db, 'users', currentUser.uid, 'expenses'), {
        merchant: expense.merchant.slice(0, 200),
        date: expense.date,
        total: parseFloat(expense.total),
        category: expense.category,
        notes: expense.notes.slice(0, 500),
        createdAt: new Date().toISOString(),
      });
      navigate('/expenses');
    } catch (err) {
      setError('Failed to save expense');
    }
    setSaving(false);
  }

  return (
    <div className="screen-container">
      <h2>Add Expense</h2>

      {/* Scan / Upload Section */}
      <div className="expense-scan-section">
        <div className="scan-buttons">
          {isNative && (
            <>
              <button
                className="scan-btn"
                onClick={() => cameraInputRef.current?.click()}
                disabled={scanning}
              >
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <circle cx="12" cy="12" r="3.2"/>
                  <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
                </svg>
                <span>Scan Receipt</span>
              </button>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={(e) => handleFile(e.target.files[0])}
              />
            </>
          )}
          <button
            className="scan-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={scanning}
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
            </svg>
            <span>Upload File</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files[0])}
          />
        </div>

        {scanning && (
          <div className="scan-progress">
            <div className="scan-spinner" />
            <span>Scanning receipt...</span>
          </div>
        )}

        {previewUrl && (
          <div className="scan-preview">
            <img src={previewUrl} alt="Receipt preview" />
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Form */}
      <div className="expense-form">
        <div className="form-group">
          <label>Merchant / Vendor</label>
          <input
            type="text"
            value={expense.merchant}
            onChange={(e) => updateField('merchant', e.target.value)}
            placeholder="e.g. Shell, AutoZone, Starbucks"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              value={expense.date}
              onChange={(e) => updateField('date', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Amount</label>
            <input
              type="number"
              value={expense.total}
              onChange={(e) => updateField('total', e.target.value)}
              placeholder="0.00"
              step="0.01"
              min="0"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Category</label>
          <div className="category-grid">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                className={`category-chip ${expense.category === cat ? 'selected' : ''}`}
                onClick={() => updateField('category', cat)}
                type="button"
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Notes (optional)</label>
          <input
            type="text"
            value={expense.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            placeholder="Trip purpose, client name, etc."
          />
        </div>

        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Expense'}
        </button>
        <button className="btn btn-secondary" onClick={() => navigate('/expenses')}>
          Cancel
        </button>
      </div>
    </div>
  );
}
